import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { ChevronLeft, ChevronRight, Star } from "lucide-react";
import { useReviews } from "../context/ReviewsContext";

function StarsRow({ value = 0, active = false }) {
  const full = Math.round(value);
  return (
    <div className="flex items-center justify-center gap-1">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          className={`h-[18px] w-[18px] ${
            i < full ? "text-orange-400" : active ? "text-white/35" : "text-gray-300"
          }`}
          fill={i < full ? "currentColor" : "none"}
          aria-hidden="true"
        />
      ))}
    </div>
  );
}

function AvatarCircle({ src, name }) {
  const [ok, setOk] = useState(true);
  const initial = (name?.trim?.()?.[0] || "A").toUpperCase();

  if (!src || !ok) {
    return (
      <div className="h-full w-full flex items-center justify-center font-semibold text-white text-3xl">
        {initial}
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={name}
      className="h-full w-full object-cover"
      loading="lazy"
      referrerPolicy="no-referrer"
      onError={() => setOk(false)}
    />
  );
}

function clampText(text = "", max = 160) {
  const t = (text || "").trim();
  if (!t) return "—";
  if (t.length <= max) return t;
  return t.slice(0, max).trim() + "…";
}

export default function Testimonials({
  limit = 9,
  autoPlay = true,
  intervalMs = 4500,
}) {
  const { reviews, loading, error, refresh } = useReviews();
  const location = useLocation();
  const isFrench = location.pathname.startsWith("/fr");

  const list = useMemo(() => {
    const arr = Array.isArray(reviews) ? reviews : [];
    const cleaned = arr.filter((r) => (r?.text || "").trim().length > 0);
    return cleaned.slice(0, limit);
  }, [reviews, limit]);

  
  const [visible, setVisible] = useState(3);

  useEffect(() => {
    const onResize = () => {
      const w = window.innerWidth;
      if (w < 640) setVisible(1);
      else if (w < 1024) setVisible(2);
      else setVisible(3);
    };
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const total = list.length;
  const pages = Math.max(1, Math.ceil(total / visible));

  const [page, setPage] = useState(0);
  const timerRef = useRef(null);

  
  useEffect(() => {
    setPage((p) => {
      const maxP = Math.max(0, pages - 1);
      return Math.min(p, maxP);
    });
  }, [pages]);

  const startIndex = page * visible;
  const slice = useMemo(() => {
    if (!total) return [];
    return list.slice(startIndex, startIndex + visible);
  }, [list, startIndex, visible, total]);

  const goPrev = () => setPage((p) => (p <= 0 ? pages - 1 : p - 1));
  const goNext = () => setPage((p) => (p >= pages - 1 ? 0 : p + 1));

  useEffect(() => {
    if (!autoPlay) return;
    if (pages <= 1) return;

    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setPage((p) => (p >= pages - 1 ? 0 : p + 1));
    }, intervalMs);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [autoPlay, intervalMs, pages]);

  return (
    <section className="bg-white py-14 md:py-20 overflow-hidden">
      <div className="max-w-[1280px] mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          {/* LEFT: Heading  */}
          <div className="lg:col-span-4">
            <p className="text-sky-500 font-bold tracking-widest text-xs mb-4">
              {isFrench ? "TÉMOIGNAGES" : "TESTIMONIALS"}
            </p>

            {isFrench ? (
              <h2 className="text-[#3b4b73] font-bold leading-[1.03]
                             text-4xl sm:text-5xl md:text-6xl">
                Ce que disent
                <br />
                nos clients
              </h2>
            ) : (
              <h2 className="text-[#3b4b73] font-bold leading-[1.03]
                             text-4xl sm:text-5xl md:text-6xl">
                What Our
                <br />
                Customers
                <br />
                Say
              </h2>
            )}

            <p className="mt-6 text-[#6b7896] text-base sm:text-[17px] leading-relaxed max-w-[360px]">
              {isFrench ? (
                <>
                  einsured.ca est entièrement numérique et utilise la technologie
                  pour simplifier le processus d'achat de{" "}
                  <span className="font-bold text-[#3b4b73]">
                    plans d'assurance vie au Canada.
                  </span>{" "}
                  Plus simple, plus intelligent et plus avantageux.
                </>
              ) : (
                <>
                  einsured.ca is fully digital, utilizing technology to simplify the
                  process of buying{" "}
                  <span className="font-bold text-[#3b4b73]">
                    life insurance plans Canada.
                  </span>{" "}
                  Simpler, smarter, and more rewarding.
                </>
              )}
            </p>

            
            {error ? (
              <div className="mt-6">
                <button
                  type="button"
                  onClick={refresh}
                  className="text-sm font-semibold text-sky-600 hover:text-sky-700 underline underline-offset-4"
                >
                  Retry loading reviews
                </button>
              </div>
            ) : null}

            
            {loading ? (
              <div className="mt-6 text-sm text-gray-400">Loading reviews…</div>
            ) : null}
          </div>

          {/* RIGHT: Cards + arrows */}
          <div className="lg:col-span-8 relative">
            
            <button
              type="button"
              onClick={goPrev}
              aria-label="Previous testimonials"
              className="hidden md:flex items-center justify-center
                         absolute left-0 top-1/2 -translate-y-1/2
                         -translate-x-[64px]
                         h-12 w-12 rounded-full
                         border border-gray-200 bg-white
                         shadow-[0_10px_26px_rgba(0,0,0,0.10)]
                         text-gray-400 hover:text-gray-700 hover:shadow-[0_14px_32px_rgba(0,0,0,0.14)]
                         transition"
            >
              <ChevronLeft className="h-6 w-6" />
            </button>

            <button
              type="button"
              onClick={goNext}
              aria-label="Next testimonials"
              className="hidden md:flex items-center justify-center
                         absolute right-0 top-1/2 -translate-y-1/2
                         translate-x-[64px]
                         h-12 w-12 rounded-full
                         border border-gray-200 bg-white
                         shadow-[0_10px_26px_rgba(0,0,0,0.10)]
                         text-gray-400 hover:text-gray-700 hover:shadow-[0_14px_32px_rgba(0,0,0,0.14)]
                         transition"
            >
              <ChevronRight className="h-6 w-6" />
            </button>

            {/* Cards row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 items-stretch auto-rows-fr">
              {slice.map((r) => {
                const name = r?.author || "Anonymous";
                const rating = Number.isFinite(Number(r?.rating)) ? Number(r?.rating) : 0;
                const text = r?.text || "";

                return (
                  <article
                    key={r.id}
                    className="group relative rounded-[34px] overflow-hidden
                               bg-[#f3f5f7]
                               shadow-[0_18px_40px_rgba(0,0,0,0.08)]
                               transition
                               hover:-translate-y-[2px]
                               hover:shadow-[0_26px_60px_rgba(0,0,0,0.12)]
                               h-full min-h-[420px]"
                  >
                    
                    <div
                      className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500
                                 bg-gradient-to-b from-[#3b93c6] to-[#4B5F86]"
                      aria-hidden="true"
                    />

                    <div className="relative px-8 pt-10 pb-10 text-center h-full flex flex-col">
                      
                      <div
                        className="mx-auto h-[96px] w-[96px] rounded-full overflow-hidden
                                   shadow-[0_12px_24px_rgba(0,0,0,0.18)]
                                   bg-gray-200
                                   group-hover:bg-white/20 transition"
                      >
                        <AvatarCircle src={r?.avatar} name={name} />
                      </div>

                      
                      <h3 className="mt-6 font-bold text-lg text-[#0f1a2b]
                                     group-hover:text-white transition-colors">
                        {name}
                      </h3>

                     
                      <div className="mt-3">
                        <StarsRow value={rating || 0} active={false} />
                      </div>

                     
                      <p
                        className="mt-4 text-sm leading-relaxed
                                   text-[#0f1a2b]
                                   group-hover:text-white/95 transition-colors
                                   flex-1 overflow-hidden"
                        style={{
                          minHeight: "84px",
                          display: "-webkit-box",
                          WebkitLineClamp: 5,
                          WebkitBoxOrient: "vertical",
                        }}
                      >
                        {clampText(text, 180)}
                      </p>
                    </div>
                  </article>
                );
              })}
            </div>

            {/* Mobile arrows */}
            <div className="mt-8 flex md:hidden items-center justify-center gap-8">
              <button
                type="button"
                onClick={goPrev}
                className="h-11 w-11 rounded-full border border-gray-200 bg-white
                           shadow-[0_10px_26px_rgba(0,0,0,0.10)]
                           text-gray-500 active:scale-95 transition"
                aria-label="Previous testimonials"
              >
                <ChevronLeft className="h-6 w-6 mx-auto" />
              </button>

              <button
                type="button"
                onClick={goNext}
                className="h-11 w-11 rounded-full border border-gray-200 bg-white
                           shadow-[0_10px_26px_rgba(0,0,0,0.10)]
                           text-gray-500 active:scale-95 transition"
                aria-label="Next testimonials"
              >
                <ChevronRight className="h-6 w-6 mx-auto" />
              </button>
            </div>

            {/* Dots */}
            <div className="mt-8 flex items-center justify-center">
              {Array.from({ length: pages }).map((_, i) => {
                const active = i === page;
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setPage(i)}
                    aria-label={`Go to testimonials page ${i + 1}`}
                    className="group flex h-6 w-6 items-center justify-center"
                  >
                    <span
                      className={`h-2.5 w-2.5 rounded-full transition ${
                        active ? "bg-sky-600" : "bg-sky-200 group-hover:bg-sky-300"
                      }`}
                    />
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
